import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import moment, { Moment } from "moment";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { failureNotification } from "@shared/store/notification.duck";
import { isCompanyAdminUser, isOfficeAdminUser } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import {
  getNoShowReport,
  NoShowLine,
  NoShowPeriod,
  NoShowPerson,
  NoShowReport,
  toCsvDownload,
} from '@features/reporting/services/ReportingService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { PRETTY_FULL_DAY_DATE_FORMAT } from '@shared/utils/DateUtils';
import { NoShowsByDayChart } from "./NoShowsByDayChart";

const ALL_OFFICES = { value: -1, label: 'All offices with automatic check-in' };

type PeriodScale = DateScale.Day | DateScale.Week | DateScale.Month;

function periodBounds(anchor: Moment, scale: PeriodScale): { from: Moment, to: Moment } {
  switch (scale) {
    case DateScale.Day: return { from: anchor.clone().startOf('day'), to: anchor.clone().endOf('day') };
    case DateScale.Week: return { from: anchor.clone().startOf('isoWeek'), to: anchor.clone().endOf('isoWeek') };
    case DateScale.Month:
    default: return { from: anchor.clone().startOf('month'), to: anchor.clone().endOf('month') };
  }
}

const PERIOD_LABELS: Record<NoShowPeriod, string> = { ALL_DAY: 'All day', AM: 'AM', PM: 'PM' };

export function NoShowReportingPage() {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const canViewReport = useMemo(() => isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser), [currentUser]);

  const [selectedOffice, setSelectedOffice] = useState<{ value: number, label: string }>(ALL_OFFICES);
  // Kept between loads so the office list doesn't empty while a new period loads
  const [offices, setOffices] = useState<{ id: number, label: string }[]>([]);
  const [scale, setScale] = useState<PeriodScale>(DateScale.Month);
  const [anchorDate, setAnchorDate] = useState<Moment>(moment());
  const {from: fromDate, to: toDate} = useMemo(() => periodBounds(anchorDate, scale), [anchorDate, scale]);
  const fromKey = fromDate.format('YYYY-MM-DD');
  const toKey = toDate.format('YYYY-MM-DD');

  const [report, setReport] = useState<NoShowReport | undefined>();
  const [loading, setLoading] = useState(false);

  const officeOptions = useMemo(() => [ALL_OFFICES, ...offices.map(o => ({ value: o.id, label: o.label }))], [offices]);
  const officeId = selectedOffice.value === ALL_OFFICES.value ? undefined : selectedOffice.value;

  useEffect(() => {
    if (!canViewReport) return;
    let cancelled = false;
    setLoading(true);
    setReport(undefined);
    getNoShowReport(moment(fromKey), moment(toKey), officeId)
      .then(data => {
        if (cancelled) return;
        setReport(data);
        setOffices(data.offices);
      })
      .catch(() => { if (!cancelled) dispatch(failureNotification("Failed to get no-shows")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canViewReport, fromKey, toKey, officeId, dispatch]);

  const downloadCsv = () => {
    if (!report || report.noShows.length === 0) return;
    const rows = report.noShows.map((line: NoShowLine) => ({
      'Date': moment(line.date).format('DD/MM/YYYY'),
      'Name': line.name,
      'Team': line.teamName ?? '',
      'Office': line.officeLabel,
      'Booked': PERIOD_LABELS[line.period],
      'Desk': line.deskLabel ?? '',
      'Checked in at': line.checkedInAt ?? '',
    }));
    const officePart = (officeId ? selectedOffice.label : 'all-offices').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    toCsvDownload(rows, `no-shows-${officePart}-${fromKey}-to-${report.countedTo}`);
  };

  if (!canViewReport) {
    return (
      <ReportingContainer>
        <GoBackButton url={"/manage/reporting/office"} />
        <AccessRequired visible={true} companyAdminsRequired={true} officeAdminsRequired={true} />
      </ReportingContainer>
    );
  }

  // Out of the bookings that are settled, so today's not-checked-in-yet don't lower the rate
  const settledBookings = report ? report.checkedIn + report.noShowCount : 0;
  const noShowRate = report && settledBookings > 0 ? `${Math.round(report.noShowCount / settledBookings * 100)}%` : '-';

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting/office"} />
      <HeadlineLarge style={{marginBottom: 8}}>No-shows</HeadlineLarge>
      <BodySmall colour={Colours.darkGrey} style={{marginBottom: 16}}>
        People booked into an office with automatic check-in who didn't check in that day. Anyone booked today who hasn't checked in yet only counts as a no-show once the day is over.
      </BodySmall>

      <ControlsRow>
        <ControlGroup>
          <BodyRegular weight={600} style={{marginBottom: 8}}>Office</BodyRegular>
          <Select options={officeOptions}
                  value={selectedOffice}
                  onChange={(item: any) => setSelectedOffice(item)}
                  styles={REACT_SELECT_DROPDOWN_STYLES}
                  isDisabled={loading}
                  isSearchable={false} />
        </ControlGroup>
        <PeriodGroup>
          <BodyRegular weight={600} style={{marginBottom: 8}}>Period</BodyRegular>
          <ScaleRow>
            <ToggleableOutlineButton isActive={scale === DateScale.Day} click={() => setScale(DateScale.Day)} text={'button.day-view'} disabled={loading} />
            <ToggleableOutlineButton isActive={scale === DateScale.Week} click={() => setScale(DateScale.Week)} text={'button.week-view'} disabled={loading} />
            <ToggleableOutlineButton isActive={scale === DateScale.Month} click={() => setScale(DateScale.Month)} text={'button.month-view'} disabled={loading} />
          </ScaleRow>
          <DateFlicker dateScale={scale}
                       updateGlobalDate={false}
                       date={anchorDate}
                       onChange={(date: Moment) => setAnchorDate(date)}
                       showDateRange={scale !== DateScale.Day}
                       dateFormat={PRETTY_FULL_DAY_DATE_FORMAT}
                       disabled={loading} />
        </PeriodGroup>
      </ControlsRow>

      {loading && (
        <Center>
          <LoadingSpinner hideBorder={true} />
        </Center>
      )}

      {!loading && report && report.offices.length === 0 && (
        <EmptyState title="No offices are set up for automatic check-in"
                    description="Add your office's network names or IP addresses under Settings > Offices and meeting rooms > Automatic Check-in. No-shows will appear here once an office is set up." />
      )}

      {!loading && report && report.offices.length > 0 && !report.countedTo && (
        <EmptyState title="Nothing to count yet"
                    description="This period hasn't started yet. Choose a period that includes today or earlier." />
      )}

      {!loading && report && report.offices.length > 0 && report.countedTo && report.bookings === 0 && (
        <EmptyState title="No one was booked in"
                    description="No one was booked into an office with automatic check-in during this period." />
      )}

      {!loading && report && report.countedTo && report.bookings > 0 && (
        <>
          <SummaryCard>
            <SummaryTitle>
              {officeId ? selectedOffice.label : 'All offices'}: {moment(report.fromDate).format('D MMM YYYY')}
              {report.countedTo !== report.fromDate && ` to ${moment(report.countedTo).format('D MMM YYYY')}`}
            </SummaryTitle>
            <SummaryGrid>
              <SummaryItem>
                <SummaryValue>{report.bookings}</SummaryValue>
                <SummaryLabel>Days booked</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{report.checkedIn}</SummaryValue>
                <SummaryLabel>Checked in</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{report.noShowCount}</SummaryValue>
                <SummaryLabel>No-shows</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{noShowRate}</SummaryValue>
                <SummaryLabel>No-show rate</SummaryLabel>
              </SummaryItem>
            </SummaryGrid>
            {report.notCheckedInYet > 0 && (
              <SummaryNote>
                {report.notCheckedInYet} booked today {report.notCheckedInYet === 1 ? "hasn't" : "haven't"} checked in yet. They'll count as no-shows if they don't by the end of the day.
              </SummaryNote>
            )}
            {report.checkedInWithoutBooking > 0 && (
              <SummaryNote>
                {report.checkedInWithoutBooking === 1 ? 'There was also 1 check-in' : `There were also ${report.checkedInWithoutBooking} check-ins`} from people who hadn't booked that office that day. These aren't included in the figures above.
              </SummaryNote>
            )}
          </SummaryCard>

          {report.noShowCount === 0 ? (
            <BodyLarge weight={600} colour={Colours.blue}>No no-shows in this period.</BodyLarge>
          ) : (
            <>
              {report.days.length > 1 && <NoShowsByDayChart days={report.days} />}

              <FlatContentCard style={{marginBottom: 24}}>
                <BodyLarge weight={600} style={{marginBottom: 4}}>Download no-shows</BodyLarge>
                <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>One row for each no-show in this period.</BodyRegular>
                <OutlineButton text="button.download-csv" click={downloadCsv} />
              </FlatContentCard>

              <SectionTitle>By person</SectionTitle>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Team</Th>
                      <Th>No-shows</Th>
                      <Th>Days booked</Th>
                      <Th>Last automatic check-in</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.people.map((person: NoShowPerson) => (
                      <Row key={person.userId}>
                        <Td style={{fontWeight: 600}}>{person.name}</Td>
                        <Td>{person.teamName || '-'}</Td>
                        <Td>{person.noShows}</Td>
                        <Td>{person.bookings}</Td>
                        <Td>
                          {person.lastCheckIn ? moment(person.lastCheckIn).format('D MMM YYYY') : (
                            <NeverCheckedIn title="No automatic check-in has been recorded for this person. Their device may not be set up.">
                              Never
                            </NeverCheckedIn>
                          )}
                        </Td>
                      </Row>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              <SectionTitle>Every no-show</SectionTitle>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Date</Th>
                      <Th>Name</Th>
                      <Th>Team</Th>
                      <Th>Office</Th>
                      <Th>Booked</Th>
                      <Th>Desk</Th>
                      <Th>Note</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.noShows.map((line: NoShowLine) => (
                      <Row key={`${line.userId}-${line.date}-${line.officeId}`}>
                        <Td>{moment(line.date).format('ddd D MMM')}</Td>
                        <Td style={{fontWeight: 600}}>{line.name}</Td>
                        <Td>{line.teamName || '-'}</Td>
                        <Td>{line.officeLabel}</Td>
                        <Td>{PERIOD_LABELS[line.period]}</Td>
                        <Td>{line.deskLabel || '-'}</Td>
                        <Td>{line.checkedInAt ? `Checked in at ${line.checkedInAt}` : ''}</Td>
                      </Row>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </>
          )}
        </>
      )}
    </ReportingContainer>
  );
}

function EmptyState(props: { title: string, description: string }) {
  return (
    <Center style={{marginTop: 32}}>
      <MountedContentCard>
        <MountedContentCardContent>
          <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{props.title}</BodyLarge>
          <BodyRegular>{props.description}</BodyRegular>
        </MountedContentCardContent>
      </MountedContentCard>
    </Center>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`

const ControlGroup = styled.div`
  width: 100%;
  @media (${tablet}) {
    max-width: 320px;
  }
`

const PeriodGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    max-width: 520px;
    margin-left: auto;
    align-items: flex-end;
    text-align: right;
  }
`

const ScaleRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 12px;
  @media (${tablet}) {
    justify-content: flex-end;
  }
`

const SummaryCard = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 0 0 16px 0;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryNote = styled.p`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.darkGrey};
  margin: 16px 0 0 0;
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const SectionTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 32px 0 0 0;
`

const TableWrapper = styled.div`
  margin-top: 8px;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  white-space: nowrap;
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
  vertical-align: top;
`

const Row = styled.tr`
  &:hover {
    background-color: ${Colours.blue10};
  }
`

const NeverCheckedIn = styled.span`
  color: ${Colours.orange};
  font-weight: 600;
  cursor: help;
`
